import { useTheme } from '@mui/material/styles';
import { FONTS } from '../../../config/app-config';
import { getResponseErrorMessage as getErrorMessage } from '../../../utils/apiErrors';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import { secureFetch } from '../../../utils/secureFetch';
import ApiKeyDialog from './dialogs/ApiKeyDialog';

const EMPTY_FORM = {
    description: '',
    ttl: 365,
    roles: ['Docs-Admin'],
};

const getKeyId = (apiKey) => apiKey._id || apiKey.id;

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getRemainingDays = (expiresAt) => {
    const expiry = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiry)) return 365;
    return Math.min(365, Math.max(1, Math.ceil((expiry - Date.now()) / 86400000)));
};

export default function ApiKeyManagement({
    open,
    isAuthenticated,
    login,
    adminApiBase,
}) {
    const theme = useTheme();
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [generatedKey, setGeneratedKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [removeAllOpen, setRemoveAllOpen] = useState(false);
    const [removingAll, setRemovingAll] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

    const notify = useCallback((message, severity = 'success') => {
        setSnack({ open: true, message, severity });
    }, []);

    const fetchApiKeys = useCallback(async () => {
        setLoading(true);
        try {
            const response = await secureFetch(`${adminApiBase}admin/api-keys`, {}, true);
            if (!response.ok) {
                throw new Error(await getErrorMessage(response, 'Failed to load API keys.'));
            }
            const data = await response.json();
            setApiKeys(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to load API keys', error);
            notify(error.message || 'Failed to load API keys.', 'error');
        } finally {
            setLoading(false);
        }
    }, [adminApiBase, notify]);

    useEffect(() => {
        if (!open) return;
        if (!isAuthenticated) {
            login();
            return;
        }
        fetchApiKeys();
    }, [open, isAuthenticated, login, fetchApiKeys]);

    const openCreateDialog = () => {
        setFormMode('create');
        setEditingId(null);
        setFormData({ ...EMPTY_FORM, roles: [...EMPTY_FORM.roles] });
        setGeneratedKey('');
        setFormOpen(true);
    };

    const openEditDialog = (apiKey) => {
        setFormMode('edit');
        setEditingId(getKeyId(apiKey));
        setFormData({
            description: apiKey.description || '',
            ttl: getRemainingDays(apiKey.expires_at),
            roles: Array.isArray(apiKey.roles) ? [...apiKey.roles] : [],
        });
        setGeneratedKey('');
        setFormOpen(true);
    };

    const closeFormDialog = () => {
        if (saving) return;
        setFormOpen(false);
        setGeneratedKey('');
    };

    const saveApiKey = async () => {
        const ttl = Number(formData.ttl);
        const payload = {
            ttl,
            roles: formData.roles,
            ...(formData.description.trim() && { description: formData.description.trim() }),
        };
        const isEditing = formMode === 'edit';
        const url = isEditing
            ? `${adminApiBase}admin/api-keys/${editingId}`
            : `${adminApiBase}admin/api-keys`;

        setSaving(true);
        try {
            const response = await secureFetch(url, {
                method: isEditing ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }, true);
            if (!response.ok) {
                throw new Error(await getErrorMessage(
                    response,
                    `Failed to ${isEditing ? 'update' : 'create'} API key.`,
                ));
            }

            if (isEditing) {
                setFormOpen(false);
                notify('API key updated.');
            } else {
                const data = await response.json();
                if (!data.apiKey) throw new Error('The API did not return the generated key.');
                setGeneratedKey(data.apiKey);
                notify('API key created. Copy it before closing the dialog.');
            }
            await fetchApiKeys();
        } catch (error) {
            console.error('Failed to save API key', error);
            notify(error.message || 'Failed to save API key.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteApiKey = async (id) => {
        if (!window.confirm('Delete this API key? This action cannot be undone.')) return;
        try {
            const response = await secureFetch(`${adminApiBase}admin/api-keys/${id}`, {
                method: 'DELETE',
            }, true);
            if (!response.ok) {
                throw new Error(await getErrorMessage(response, 'Failed to delete API key.'));
            }
            setApiKeys((keys) => keys.filter((apiKey) => getKeyId(apiKey) !== id));
            notify('API key deleted.');
        } catch (error) {
            console.error('Failed to delete API key', error);
            notify(error.message || 'Failed to delete API key.', 'error');
            fetchApiKeys();
        }
    };

    const removeAllApiKeys = async () => {
        setRemovingAll(true);
        try {
            const response = await secureFetch(`${adminApiBase}admin/api-keys`, {
                method: 'DELETE',
            }, true);
            if (!response.ok) {
                throw new Error(await getErrorMessage(response, 'Failed to delete all API keys.'));
            }
            setRemoveAllOpen(false);
            notify('All API keys removed.');
            await fetchApiKeys();
        } catch (error) {
            notify(error.message || 'Failed to delete all API keys.', 'error');
        } finally {
            setRemovingAll(false);
        }
    };

    const copyGeneratedKey = async () => {
        try {
            await navigator.clipboard.writeText(generatedKey);
            notify('API key copied to clipboard.');
        } catch {
            notify('Could not copy the API key. Select and copy it manually.', 'error');
        }
    };

    return (
        <>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                    variant="h6"
                    sx={{ fontFamily: FONTS.heading, fontWeight: 700, mt: 2 }}
                >
                    Manage API Keys
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
                    <Button
                        variant="contained"
                        onClick={openCreateDialog}
                        sx={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}
                    >
                        + Add new API Key
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setRemoveAllOpen(true)}
                        disabled={apiKeys.length === 0 || loading}
                    >
                        Remove all keys
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : apiKeys.length === 0 ? (
                    <Alert severity="info">No API keys have been created.</Alert>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Creator</TableCell>
                                    <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Created</TableCell>
                                    <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Expires</TableCell>
                                    <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Description</TableCell>
                                    <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Roles</TableCell>
                                    <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiKeys.map((apiKey) => {
                                    const id = getKeyId(apiKey);
                                    return (
                                        <TableRow key={id} hover>
                                            <TableCell>{apiKey.creator || '—'}</TableCell>
                                            <TableCell>{formatDate(apiKey.created_at)}</TableCell>
                                            <TableCell>{formatDate(apiKey.expires_at)}</TableCell>
                                            <TableCell>{apiKey.description || '—'}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {(apiKey.roles || []).map((role) => (
                                                        <Chip key={role} label={role} size="small" />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Button
                                                    size="small"
                                                    sx={{ color: 'accent.main' }}
                                                    onClick={() => openEditDialog(apiKey)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button size="small" color="error" onClick={() => deleteApiKey(id)}>
                                                    Delete
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            <ApiKeyDialog
                open={formOpen}
                mode={formMode}
                formData={formData}
                generatedKey={generatedKey}
                saving={saving}
                onChange={setFormData}
                onClose={closeFormDialog}
                onSave={saveApiKey}
                onCopy={copyGeneratedKey}
            />

            <Dialog
                open={removeAllOpen}
                onClose={removingAll ? undefined : () => setRemoveAllOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Remove all API keys?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will permanently remove all {apiKeys.length} API key{apiKeys.length === 1 ? '' : 's'}.
                        Applications using these keys will immediately lose access.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveAllOpen(false)} disabled={removingAll}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={removeAllApiKeys}
                        disabled={removingAll}
                        sx={{ minWidth: 108 }}
                    >
                        {removingAll ? <CircularProgress size={20} color="inherit" /> : 'Remove all'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((current) => ({ ...current, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snack.severity}
                    variant="filled"
                    onClose={() => setSnack((current) => ({ ...current, open: false }))}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    );
}
